// <!-- GRACE:MODULE_START name="ui_asr" -->
// <!-- GRACE:DEPENDENCY target="ui_types" -->
import React from 'react';
import { MicrophoneIcon, StopIcon } from '@heroicons/react/24/solid';
import { DictationButtonPropsExtended } from '../types/asyncProcessing';

// <!-- GRACE:CONTRACT function="DictationButton" -->
const DictationButton: React.FC<DictationButtonPropsExtended> = ({
  isRecording,
  processingState,
  onStart,
  onStop
}) => {
  const handleClick = () => {
    if (isRecording) {
      onStop();
    } else {
      onStart();
    }
  };

  const getButtonState = () => {
    if (isRecording) return 'recording';
    if (processingState.status === 'uploading') return 'uploading';
    if (processingState.status === 'processing') return 'processing';
    if (processingState.status === 'error') return 'error';
    return 'idle';
  };

  const getButtonText = () => {
    switch (getButtonState()) {
      case 'recording': return 'Остановить';
      case 'uploading': return 'Загрузка...';
      case 'processing': return 'Обработка...';
      case 'error': return 'Ошибка';
      default: return 'Диктовать';
    }
  };

  const isDisabled = processingState.status === 'uploading' || processingState.status === 'processing';

  return (
    <button
      onClick={handleClick}
      disabled={isDisabled}
      className={`
        relative w-24 h-24 rounded-full flex items-center justify-center
        transition-all duration-300 transform hover:scale-105
        focus:outline-none focus:ring-4 focus:ring-opacity-50
        ${getButtonState() === 'recording' 
          ? 'bg-medical-accent hover:bg-red-700 focus:ring-red-300' 
          : getButtonState() === 'error'
          ? 'bg-red-500 hover:bg-red-600 focus:ring-red-300'
          : 'bg-medical-primary hover:bg-blue-700 focus:ring-blue-300'
        }
        ${isDisabled 
          ? 'opacity-50 cursor-not-allowed hover:scale-100' 
          : 'cursor-pointer'
        }
        ${getButtonState() === 'recording' ? 'recording-pulse' : ''}
      `}
      aria-label={isRecording ? 'Остановить запись' : 'Начать запись'}
    >
      {/* Иконка микрофона или стоп */}
      <div className="text-white">
        {isRecording ? (
          <StopIcon className="w-8 h-8" />
        ) : (
          <MicrophoneIcon className="w-8 h-8" />
        )}
      </div>

      {/* Индикатор загрузки */}
      {processingState.status === 'uploading' && (
        <div className="absolute -top-2 -left-2 w-6 h-6 bg-gray-600 rounded-full flex items-center justify-center">
          <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
        </div>
      )}

      {/* Индикатор обработки */}
      {processingState.status === 'processing' && (
        <div className="absolute -top-2 -left-2 w-6 h-6 bg-gray-600 rounded-full flex items-center justify-center">
          <div className="w-2 h-2 bg-white rounded-full animate-spin"></div>
        </div>
      )}

      {/* Индикатор ошибки */}
      {processingState.status === 'error' && (
        <div className="absolute -top-2 -left-2 w-6 h-6 bg-red-600 rounded-full flex items-center justify-center">
          <span className="text-white text-xs">!</span>
        </div>
      )}

      {/* Индикатор записи */}
      {isRecording && (
        <div className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
          <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
        </div>
      )}

      {/* Текст под кнопкой */}
      <div className="absolute -bottom-5 left-1/2 transform -translate-x-1/2 text-center">
        <p className="text-sm font-medium text-medical-text whitespace-nowrap">
          {getButtonText()}
        </p>
      </div>
    </button>
  );
};

export default DictationButton;
// <!-- GRACE:MODULE_END name="ui_asr" -->
